import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Menu, Settings, Trash2, X,
    CheckCircle, AlertCircle, Database, Zap, FileText,
    Shuffle, Activity, XCircle, Terminal, Book, ArrowLeft,
    Network, ExternalLink, Globe, ShieldCheck,
    User, ChevronDown, Info, LifeBuoy, LayoutDashboard
} from 'lucide-react';
import './EnterpriseDMS.css';


import DMSConnections from '../components/dms/DMSConnections';
import DMSMigrations from '../components/dms/DMSMigrations';
import DMSMonitor from '../components/dms/DMSMonitor';
import PrivateConnectivity from '../components/dms/PrivateConnectivity';
import ConnectivityTest from '../components/dms/ConnectivityTest';
import AlertWidget from '../components/dms/AlertWidget';
import ThemeToggle from '../components/ThemeToggle';
import SettingsPage from './SettingsPage';
import ProfilePage from './ProfilePage';
import ProfileSettingsModal from './ProfileSettingsModal';
import FeaturesPage from './FeaturesPage';
import DMSDashboard from '../components/dms/DMSDashboard';
import ConnectionWizard from '../components/dms/ConnectionWizard';
import AnimatedLogo from '../components/AnimatedLogo';
import Dock from '../components/Dock';
import awsLogo from '../assets/logos/aws-logo.svg';
import gcpLogo from '../images/gcp-logo.png';
import azureLogo from '../assets/logos/azure-logo.svg';
import { ParticleCard } from '../components/MagicBento';

import { API_BASE_URL } from '../config';

const defaultConnectionState = {
    name: '',
    type: 'postgresql',
    host: '',
    port: 5432,
    username: '',
    password: '',
    ssl_enabled: false,
    connectivity_mode: 'public',
    private_connectivity_method: 'ssh',
    ssh_host: '',
    ssh_port: 22,
    ssh_username: 'ec2-user',
    ssh_private_key: '',
    allocated_port: null
};

const NotificationToast = ({ notify, onClose }) => {
    useEffect(() => {
        if (notify) {
            const timer = setTimeout(onClose, 3000);
            return () => clearTimeout(timer);
        }
    }, [notify, onClose]);

    if (!notify) return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            borderRadius: '8px',
            background: 'var(--bg-card)',
            border: `1px solid ${notify.type === 'error' ? 'var(--error-color)' : 'var(--success-color)'}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            animation: 'slideInUp 0.3s ease-out',
            color: 'var(--text-primary)'
        }}>
            {notify.type === 'success' ? (
                <CheckCircle size={20} color="var(--success-color)" />
            ) : (
                <AlertCircle size={20} color="var(--error-color)" />
            )}
            <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{notify.message}</span>
            <button
                onClick={onClose}
                style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '4px',
                    marginLeft: '8px'
                }}
            >
                <X size={16} />
            </button>
        </div>
    );
};

const resolvePhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const path = url.startsWith('/') ? url : '/' + url;
    return `${base}${path}`;
};

const EnterpriseDMS = ({ onBack, theme, toggleTheme, onOpenSettings, initialView }) => {
    const readAuthUser = () => {
        try {
            const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
            if (token) {
                const base64Url = token.split('.')[1];
                if (base64Url) {
                    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    while (base64.length % 4) base64 += '=';
                    const payload = JSON.parse(decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));

                    let name = 'Profile';
                    if (payload && payload.name) name = payload.name;
                    else if (payload && payload.email) name = payload.email.split('@')[0];

                    let photoUrl = null;
                    if (payload && payload.picture) photoUrl = payload.picture;
                    else if (payload && payload.avatar) photoUrl = payload.avatar;

                    const resolvedUrl = resolvePhotoUrl(photoUrl);
                    return { ...payload, name, photoUrl: resolvedUrl, picture: resolvedUrl || payload.picture || payload.avatar };
                }
            }
        } catch (e) {
            console.error('Failed to parse auth token for header user:', e);
        }
        return { name: 'Profile', photoUrl: null };
    };

    // Reactive auth user — re-reads whenever localStorage is updated (e.g. after SSO redirect)
    const [authUser, setAuthUser] = useState(readAuthUser);
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        const fetchMe = async () => {
            try {
                const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
                if (token) {
                    const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data.success && data.user) {
                            const resolvedUrl = resolvePhotoUrl(data.user.picture || data.user.avatar_url);
                            setAuthUser(prev => ({
                                ...prev,
                                photoUrl: resolvedUrl || prev.photoUrl,
                                picture: resolvedUrl || prev.picture
                            }));
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to fetch /me:', err);
            }
        };

        const handleStorage = () => {
            setAuthUser(readAuthUser());
            setImgError(false);
            fetchMe();
        };
        // 'storage' fires in OTHER tabs; 'dms-auth-updated' fires in THIS tab after SSO redirect
        window.addEventListener('storage', handleStorage);
        window.addEventListener('dms-auth-updated', handleStorage);

        // Initial fetch on mount
        fetchMe();

        return () => {
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('dms-auth-updated', handleStorage);
        };
    }, []);

    const [activeView, setActiveView] = useState(() => {
        // Priority: Initial Prop > Hash > LocalStorage > Default
        if (initialView) {
            if (initialView === 'drafts') return 'connections';
            return initialView;
        }

        const hash = window.location.hash;
        if (hash.startsWith('#/dms/')) {
            const view = hash.replace('#/dms/', '');
            if (view === 'drafts') return 'connections';
            return view;
        }

        const stored = localStorage.getItem('dms_activeView') || 'dashboard';
        if (stored === 'drafts') return 'connections';
        return stored;
    });

    const [connectionsTab, setConnectionsTab] = useState(() => {
        const hash = window.location.hash;
        if (hash === '#/dms/drafts') return 'drafts';
        return 'active';
    });

    // Update hash and storage when view changes
    useEffect(() => {
        localStorage.setItem('dms_activeView', activeView);

        // Only hijack the hash if we are actually in a DMS-related URL
        // or if it's currently a non-DMS URL that we need to fix.
        // DO NOT change hash if it's #/docs
        const currentHash = window.location.hash;
        if (currentHash === '#/docs') return;

        if (activeView === 'connections' && connectionsTab === 'drafts') {
            if (currentHash !== '#/dms/drafts') {
                window.location.hash = '#/dms/drafts';
            }
        } else if (currentHash !== `#/dms/${activeView}`) {
            window.location.hash = `#/dms/${activeView}`;
        }
    }, [activeView, connectionsTab]);

    // Handle external hash changes (e.g. browser back button)
    useEffect(() => {
        const handleHash = () => {
            const hash = window.location.hash;
            if (hash.startsWith('#/dms/')) {
                const view = hash.replace('#/dms/', '');
                if (view === 'drafts') {
                    setActiveView('connections');
                    setConnectionsTab('drafts');
                } else if (view !== activeView) {
                    setActiveView(view);
                    if (view === 'connections') {
                        setConnectionsTab('active');
                    }
                }
            }
        };
        window.addEventListener('hashchange', handleHash);
        return () => window.removeEventListener('hashchange', handleHash);
    }, [activeView]);

    const [jobs, setJobs] = useState([]);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [connections, setConnections] = useState([]);

    // ── Local Storage Draft Helpers ───────────────────────────────────────────
    const getLocalDrafts = useCallback(() => {
        try {
            const stored = localStorage.getItem('dms_connection_drafts');
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            console.error('Failed to parse local drafts', e);
            return [];
        }
    }, []);

    const saveLocalDraft = useCallback((draft) => {
        try {
            const drafts = getLocalDrafts();
            const index = drafts.findIndex(d => d.id === draft.id);
            const updatedDraft = {
                ...draft,
                updated_at: new Date().toISOString()
            };
            if (index > -1) {
                updatedDraft.created_at = draft.created_at || drafts[index].created_at || new Date().toISOString();
                drafts[index] = updatedDraft;
            } else {
                updatedDraft.created_at = draft.created_at || new Date().toISOString();
                drafts.push(updatedDraft);
            }
            localStorage.setItem('dms_connection_drafts', JSON.stringify(drafts));
        } catch (e) {
            console.error('Failed to save local draft', e);
        }
    }, [getLocalDrafts]);

    const deleteLocalDraft = useCallback((id) => {
        try {
            const drafts = getLocalDrafts();
            const filtered = drafts.filter(d => d.id !== id);
            localStorage.setItem('dms_connection_drafts', JSON.stringify(filtered));
        } catch (e) {
            console.error('Failed to delete local draft', e);
        }
    }, [getLocalDrafts]);

    const fetchConnections = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/connections?platform=dms`, { cache: 'no-store' });
            const data = await res.json();
            if (data.success) {
                // Filter out backend connection drafts to avoid duplicate rendering/conflicts
                const backendConns = data.connections.filter(conn => {
                    if (!conn.options) return true;
                    try {
                        const opts = typeof conn.options === 'string' ? JSON.parse(conn.options) : conn.options;
                        const isDraft = opts && (opts.is_draft === true || opts.isDraft === true);
                        return !isDraft;
                    } catch (e) {
                        return true;
                    }
                });
                const localDrafts = getLocalDrafts();
                setConnections([...backendConns, ...localDrafts]);
            }
        } catch (err) {
            console.error(err);
            // Fallback to local drafts even if backend is offline
            const localDrafts = getLocalDrafts();
            setConnections(localDrafts);
        }
    }, [getLocalDrafts]);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Modal States
    const [showConnectionModal, setShowConnectionModal] = useState(false);
    const [showConnectionEditSelectionModal, setShowConnectionEditSelectionModal] = useState(false);
    const [pendingConnectionEdit, setPendingConnectionEdit] = useState(null);
    const [connectionEditContext, setConnectionEditContext] = useState(null);
    const [connectionFormData, setConnectionFormData] = useState(defaultConnectionState);
    const [editingConnectionId, setEditingConnectionId] = useState(null);

    // Auto-save draft state
    const [autoSaveDraftId, setAutoSaveDraftId] = useState(
        () => sessionStorage.getItem('dms_autosave_draft_id') || null
    );
    const autoSaveTimerRef = useRef(null);
    const [autoSaveToast, setAutoSaveToast] = useState(false);
    const autoSaveToastTimerRef = useRef(null);

    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [editContext, setEditContext] = useState(null);
    const [step, setStep] = useState(1);
    const [editingJobId, setEditingJobId] = useState(null);
    const [validationResult, setValidationResult] = useState(null);

    const [formData, setFormData] = useState({
        sourceId: '',
        targetId: '',
        migrationType: 'full+cdc',
        cloudRegion: 'us-east-1',
        secureTunnel: false,
        parallelism: 4,
        connectivityMode: 'public'
    });

    const [loading, setLoading] = useState(false);
    const [testSteps, setTestSteps] = useState({ source: 'pending', target: 'pending', bridge: 'pending' });
    const [notify, setNotify] = useState(null);
    const [activeDrawer, setActiveDrawer] = useState(null); // 'settings' | 'profile' | null
    const [showProfileSettings, setShowProfileSettings] = useState(false);

    // ── Auto-save helpers ──────────────────────────────────────────────────────
    const showAutoSaveToast = useCallback(() => {
        setAutoSaveToast(true);
        clearTimeout(autoSaveToastTimerRef.current);
        autoSaveToastTimerRef.current = setTimeout(() => setAutoSaveToast(false), 2200);
    }, []);

    // Checks if the form has enough content to be worth auto-saving (any modified or non-default field)
    const hasAutoSaveContent = useCallback((data) => {
        if (!data) return false;
        return !!(
            (data.name && data.name.trim() !== '') ||
            (data.host && data.host.trim() !== '') ||
            (data.username && data.username.trim() !== '') ||
            (data.password && data.password.trim() !== '') ||
            data.ssl_enabled !== false ||
            (data.ssh_host && data.ssh_host.trim() !== '') ||
            (data.ssh_private_key && data.ssh_private_key.trim() !== '') ||
            data.type !== 'postgresql' ||
            data.port !== 5432 ||
            data.connectivity_mode !== 'public' ||
            data.private_connectivity_method !== 'ssh' ||
            data.ssh_port !== 22 ||
            data.ssh_username !== 'ec2-user'
        );
    }, []);

    // Perform the local-only auto-save
    const performAutoSave = useCallback((formData, existingDraftId) => {
        if (!hasAutoSaveContent(formData)) return;
        try {
            const now = new Date();
            const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
            const draftName = formData.name || `Untitled Draft — ${timeStr}`;

            const draftId = existingDraftId || `dms_draft_${Date.now()}`;

            const currentOptions = typeof formData.options === 'string'
                ? JSON.parse(formData.options)
                : (formData.options || {});

            const draftPayload = {
                ...formData,
                id: draftId,
                name: draftName,
                options: {
                    ...currentOptions,
                    is_draft: true,
                    auto_saved: true
                },
                platform: 'dms'
            };

            saveLocalDraft(draftPayload);

            if (!existingDraftId) {
                setAutoSaveDraftId(draftId);
                sessionStorage.setItem('dms_autosave_draft_id', draftId);
            }
            fetchConnections();
            showAutoSaveToast();
        } catch (e) {
            console.warn('[AutoSave] Failed:', e.message);
        }
    }, [hasAutoSaveContent, saveLocalDraft, fetchConnections, showAutoSaveToast]);

    // Auto-save interval has been removed. Auto-saving now only occurs explicitly 
    // when clicking 'Save as Draft' to prevent creating duplicate/unwanted drafts on modal open.

    // Flush auto-save immediately when modal closes (user navigated away)
    const handleCloseConnectionModal = useCallback(() => {
        clearTimeout(autoSaveTimerRef.current);
        setAutoSaveDraftId(null);
        sessionStorage.removeItem('dms_autosave_draft_id');
        setShowConnectionModal(false);
        setConnectionEditContext(null);
        setPendingConnectionEdit(null);
    }, [connectionFormData, autoSaveDraftId, hasAutoSaveContent, performAutoSave]);
    // ─────────────────────────────────────────────────────────────────────────

    const handleShowNotification = (message, type = 'success') => {
        setNotify({ type, message });
    };


    // Monitor Specific State
    const [throughputData, setThroughputData] = useState([]);
    const [selectedMonitorJobId, setSelectedMonitorJobId] = useState(null);
    const [logs, setLogs] = useState([]);
    const [jobMetrics, setJobMetrics] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [jobsToDelete, setJobsToDelete] = useState([]);
    const [showConnectionDeleteModal, setShowConnectionDeleteModal] = useState(false);
    const [connectionToDelete, setConnectionToDelete] = useState(null);
    const [connectionDeleteConfirmText, setConnectionDeleteConfirmText] = useState('');
    const [connectionDeleteError, setConnectionDeleteError] = useState(null);
    const [serverIP, setServerIP] = useState('Loading...');
    const [connectionMode, setConnectionMode] = useState('public'); // 'public' | 'private'
    const [sshSourceConfig, setSSHSourceConfig] = useState({ bastionHost: '', bastionUser: 'ec2-user', keyPath: '', dbHostname: '', localPort: '5433' });
    const [sshTargetConfig, setSSHTargetConfig] = useState({ bastionHost: '', bastionUser: 'ec2-user', keyPath: '', dbHostname: '', localPort: '5434' });
    const [vpnStatus, setVpnStatus] = useState(null);
    const [cloudRegions, setCloudRegions] = useState({ aws: [], gcp: [], azure: [] });
    const [notifiedTaskTime, setNotifiedTaskTime] = useState(Date.now()); // initialize with current time to avoid notifying for old tasks on load

    // Fetch VPN Status for the modal
    const fetchVpnStatus = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vpn/status`);
            const data = await res.json();
            if (data.success) setVpnStatus(data);
        } catch (e) { console.error('Failed to fetch VPN status', e); }
    }, []);

    useEffect(() => {
        fetchVpnStatus();
    }, [fetchVpnStatus]);

    // Global VPN Task Notifications
    useEffect(() => {
        const fetchVpnTaskStatus = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/vpn/task-status`);
                const data = await res.json();
                if (data.success && data.task && data.task.status !== 'running') {
                    if (data.task.timestamp > notifiedTaskTime) {
                        const cmdName = data.task.command.charAt(0).toUpperCase() + data.task.command.slice(1);
                        if (data.task.status === 'success') {
                            setNotify({ type: 'success', message: `VPN ${cmdName} completed successfully!` });
                            fetchVpnStatus(); // update badge globally
                        } else {
                            setNotify({ type: 'error', message: `VPN ${cmdName} failed! Check logs.` });
                        }
                        setNotifiedTaskTime(data.task.timestamp);
                    }
                }
            } catch (e) { }
        };
        const interval = setInterval(fetchVpnTaskStatus, 3000);
        return () => clearInterval(interval);
    }, [notifiedTaskTime, fetchVpnStatus]);

    // Fetch Cloud Regions dynamically
    const fetchCloudRegions = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/dms/regions`);
            const data = await res.json();
            if (data.success) {
                setCloudRegions(data.regions);
            }
        } catch (e) {
            console.error('Failed to fetch cloud regions', e);
        }
    }, []);

    useEffect(() => {
        fetchCloudRegions();
    }, [fetchCloudRegions]);

    // Fetch Server Public IP
    useEffect(() => {
        const fetchIP = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/dms/server-ip`);
                const data = await res.json();
                if (data.success) setServerIP(data.ip);
            } catch (e) {
                console.error('Failed to fetch server IP', e);
                setServerIP('34.47.241.76'); // Fallback
            }
        };
        fetchIP();
    }, []);

    const fetchJobs = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/dms/pipelines`);
            const data = await res.json();
            if (data.success) {
                const stoppedJobs = JSON.parse(localStorage.getItem('dms_stopped_jobs') || '[]');
                const mappedJobs = data.jobs.map(job => {
                    if (stoppedJobs.includes(job.id) || stoppedJobs.includes(String(job.id))) {
                        return { ...job, status: 'stopped' };
                    }
                    return job;
                });
                setJobs(mappedJobs);
            }
        } catch (err) { console.error(err); }
    }, []);


    useEffect(() => {
        fetchJobs();
        fetchConnections();
        const interval = setInterval(fetchJobs, 2000);
        return () => clearInterval(interval);
    }, [fetchJobs, fetchConnections]);

    useEffect(() => {
        const fetchThroughput = async () => {
            try {
                const url = selectedMonitorJobId
                    ? `${API_BASE_URL}/api/migrations/stats/throughput?jobId=${selectedMonitorJobId}`
                    : `${API_BASE_URL}/api/migrations/stats/throughput?platform=dms`;
                const response = await fetch(url);
                const data = await response.json();
                if (data.success) setThroughputData(data.stats);
            } catch (err) { console.error(err); }
        };
        fetchThroughput();
        const interval = setInterval(fetchThroughput, 2000);
        return () => clearInterval(interval);
    }, [selectedMonitorJobId]);

    useEffect(() => {
        let interval;
        if (selectedMonitorJobId && activeView === 'monitor') {
            const fetchLogs = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/api/migrations/${selectedMonitorJobId}/logs`);
                    const data = await response.json();
                    if (data.success) setLogs(data.logs);
                } catch (err) { console.error(err); }
            };
            fetchLogs();
            interval = setInterval(fetchLogs, 3000);
        } else {
            setLogs([]);
        }
        return () => clearInterval(interval);
    }, [selectedMonitorJobId, activeView]);

    // Fetch real-time telemetry metrics for the Enterprise Dashboard
    useEffect(() => {
        if (!selectedMonitorJobId) { setJobMetrics(null); return; }
        const fetchMetrics = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/dms/metrics/${selectedMonitorJobId}`);
                const data = await response.json();
                if (data.success) setJobMetrics(data);
            } catch (err) { console.error('[Metrics]', err); }
        };
        fetchMetrics();
        const interval = setInterval(fetchMetrics, 5000);
        return () => clearInterval(interval);
    }, [selectedMonitorJobId]);

    // Navigation Hook for Components
    useEffect(() => {
        window.onSwitchToMonitor = (jobId) => {
            setActiveView('monitor');
            setSelectedMonitorJobId(jobId);
        };
        return () => {
            if (window.onSwitchToMonitor) {
                window.onSwitchToMonitor = null;
            }
        };
    }, []);

    // Connection Handlers
    const handleConnectionInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setConnectionFormData(prev => {
            let newState = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };

            // Sync SSL fields
            if (name === 'ssl_enabled') {
                newState.ssl_mode = checked ? (prev.ssl_mode === 'disable' || prev.ssl_mode === 'none' ? 'require' : prev.ssl_mode) : 'disable';
            } else if (name === 'ssl_mode') {
                newState.ssl_enabled = value !== 'disable' && value !== 'none';
            }

            // Infer database type from engine selection
            if (name === 'database_engine' && value) {
                const lowerValue = value.toLowerCase();
                if (lowerValue.includes('postgre')) newState.type = 'postgresql';
                else if (lowerValue.includes('mysql')) newState.type = 'mysql';
                else if (lowerValue.includes('mariadb')) newState.type = 'mysql';
                else if (lowerValue.includes('oracle')) newState.type = 'oracle';
                else if (lowerValue.includes('sql server')) newState.type = 'mssql';

                // Infer cloud provider
                const isAWS = lowerValue.includes('amazon') || lowerValue.includes('aws');
                const isGCP = lowerValue.includes('cloud sql') || lowerValue.includes('gcp');
                const isAzure = lowerValue.includes('azure');

                if (isAWS) newState.cloud_provider = 'aws';
                else if (isGCP) newState.cloud_provider = 'gcp';
                else if (isAzure) newState.cloud_provider = 'azure';
                else newState.cloud_provider = 'on-premise';
            }

            return newState;
        });
    };

    const handleEditConnection = (connection) => {
        const isDraft = connection.id && String(connection.id).startsWith('dms_draft_');
        setConnectionFormData({
            ...connection,
            password: connection.password || '',
            database_engine: connection.database_engine || '',
            use_ssh_tunnel: connection.use_ssh_tunnel || false,
            ssh_host: connection.ssh_host || '',
            ssh_port: connection.ssh_port || 22,
            ssh_username: connection.ssh_username || 'ubuntu',
            ssh_private_key: connection.ssh_private_key || '',
            connectivity_mode: connection.connectivity_mode || 'public',
            private_connectivity_method: connection.private_connectivity_method || (connection.connectivity_mode === 'private' ? 'ssh' : null)
        });
        setEditingConnectionId(connection.id);
        if (isDraft) {
            setAutoSaveDraftId(connection.id);
            sessionStorage.setItem('dms_autosave_draft_id', connection.id);
        } else {
            setAutoSaveDraftId(null);
            sessionStorage.removeItem('dms_autosave_draft_id');
        }
        setPendingConnectionEdit(connection);
        setShowConnectionEditSelectionModal(true);
    };

    const confirmConnectionEditContext = (context) => {
        setConnectionEditContext(context);
        setShowConnectionEditSelectionModal(false);
        setShowConnectionModal(true);
    };

    const handleCloseConnectionEditSelection = () => {
        setShowConnectionEditSelectionModal(false);
        setPendingConnectionEdit(null);
        setEditingConnectionId(null);
        setAutoSaveDraftId(null);
        sessionStorage.removeItem('dms_autosave_draft_id');
    };

    const handleOpenCreateModal = () => {
        setConnectionFormData(defaultConnectionState);
        setEditingConnectionId(null);
        setConnectionEditContext(null);
        setAutoSaveDraftId(null);
        sessionStorage.removeItem('dms_autosave_draft_id');
        setShowConnectionModal(true);
    };

    // ── Wizard: create both source + destination sequentially ─────────────────
    const handleCreateBothConnections = async ({ source, destination }) => {
        setLoading(true);
        try {
            const saveOne = async (conn) => {
                const isDraft = editingConnectionId && String(editingConnectionId).startsWith('dms_draft_');
                const url = (editingConnectionId && !isDraft)
                    ? `${API_BASE_URL}/api/connections/${editingConnectionId}`
                    : `${API_BASE_URL}/api/connections`;
                const method = (editingConnectionId && !isDraft) ? 'PUT' : 'POST';
                const payload = { ...conn, options: { is_draft: false, auto_saved: false }, platform: 'dms' };
                const res = await fetch(url, {
                    method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const data = await res.json();
                if (!data.success) throw new Error(data.error || 'Failed to save connection');
                return data;
            };

            await saveOne(source);
            await saveOne(destination);

            // Clean up any auto-save drafts
            if (autoSaveDraftId) {
                deleteLocalDraft(autoSaveDraftId);
                setAutoSaveDraftId(null);
                sessionStorage.removeItem('dms_autosave_draft_id');
            }
            fetchConnections();
            setNotify({ type: 'success', message: 'Source & Destination connections created successfully!' });
            // Don't close modal here — wizard Step 4 shows success state, user clicks Done
        } catch (err) {
            setNotify({ type: 'error', message: err.message });
            throw err; // re-throw so wizard Step 4 can display its own error state
        } finally {
            setLoading(false);
        }
    };


    const handleUpdateSingleConnection = async (conn) => {
        setLoading(true);
        try {
            const isDraft = editingConnectionId && String(editingConnectionId).startsWith('dms_draft_');
            const url = (editingConnectionId && !isDraft)
                ? `${API_BASE_URL}/api/connections/${editingConnectionId}`
                : `${API_BASE_URL}/api/connections`;
            const method = (editingConnectionId && !isDraft) ? 'PUT' : 'POST';
            const payload = { ...conn, options: { is_draft: false, auto_saved: false }, platform: 'dms' };
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Failed to update connection');

            // Clean up any auto-save drafts
            if (autoSaveDraftId) {
                deleteLocalDraft(autoSaveDraftId);
                setAutoSaveDraftId(null);
                sessionStorage.removeItem('dms_autosave_draft_id');
            }
            fetchConnections();
            setNotify({ type: 'success', message: 'Connection updated successfully!' });
            handleCloseConnectionModal(); // We can close directly for single update
        } catch (err) {
            setNotify({ type: 'error', message: err.message });
            throw err;
        } finally {
            setLoading(false);
        }
    };


    // ── Wizard: save draft for current step's form ─────────────────────────────
    const handleSaveConnection = async (isDraft = false) => {
        const draftMode = isDraft === true;
        setLoading(true);
        try {
            if (draftMode) {
                const now = new Date();
                const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
                const draftName = connectionFormData.name || `Untitled Draft — ${timeStr}`;
                const draftId = (editingConnectionId && String(editingConnectionId).startsWith('dms_draft_'))
                    ? editingConnectionId
                    : (autoSaveDraftId || `dms_draft_${Date.now()}`);
                const currentOptions = typeof connectionFormData.options === 'string'
                    ? JSON.parse(connectionFormData.options)
                    : (connectionFormData.options || {});
                const draftPayload = {
                    ...connectionFormData,
                    id: draftId,
                    name: draftName,
                    options: { ...currentOptions, is_draft: true, auto_saved: false },
                    platform: 'dms'
                };
                saveLocalDraft(draftPayload);
                setNotify({ type: 'success', message: 'Connection Draft Saved Successfully' });
                setAutoSaveDraftId(null);
                sessionStorage.removeItem('dms_autosave_draft_id');
                fetchConnections();
                setShowConnectionModal(false);
                return;
            }

            const requiredFields = ['name', 'type', 'host', 'port', 'username', 'password'];
            const missingFields = requiredFields.filter(field => !connectionFormData[field]);
            if (missingFields.length > 0) {
                const formattedFields = missingFields.map(f => f.charAt(0).toUpperCase() + f.slice(1).replace('_', ' ')).join(', ');
                setNotify({ type: 'error', message: `Please fill in all required fields: ${formattedFields}` });
                setLoading(false);
                return;
            }

            const isEditingDraft = editingConnectionId && String(editingConnectionId).startsWith('dms_draft_');
            const url = (editingConnectionId && !isEditingDraft)
                ? `${API_BASE_URL}/api/connections/${editingConnectionId}`
                : `${API_BASE_URL}/api/connections`;
            const method = (editingConnectionId && !isEditingDraft) ? 'PUT' : 'POST';
            const currentOptions = typeof connectionFormData.options === 'string'
                ? JSON.parse(connectionFormData.options)
                : (connectionFormData.options || {});
            const payload = {
                ...connectionFormData,
                options: { ...currentOptions, is_draft: false, auto_saved: false },
                platform: 'dms'
            };
            if (isEditingDraft) delete payload.id;

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                setNotify({ type: 'success', message: `Connection ${editingConnectionId && !isEditingDraft ? 'Updated' : 'Created'} Successfully` });
                if (isEditingDraft) deleteLocalDraft(editingConnectionId);
                if (autoSaveDraftId && autoSaveDraftId !== String(editingConnectionId)) {
                    deleteLocalDraft(autoSaveDraftId);
                    setAutoSaveDraftId(null);
                    sessionStorage.removeItem('dms_autosave_draft_id');
                }
                fetchConnections();
                setShowConnectionModal(false);
            } else {
                throw new Error(data.error || 'Operation failed');
            }
        } catch (err) {
            setNotify({ type: 'error', message: err.message });
        } finally {
            setLoading(false);
        }
    };


    // ... (Keep existing Wizard Handlers: handleValidateConnection, handleStartMigration) ...
    const handleValidateConnection = async () => {
        setLoading(true);
        setValidationResult(null);
        setTestSteps({ source: 'loading', target: 'pending', bridge: 'pending' });

        try {
            // Accumulate warnings and CDC errors from all steps
            let allWarnings = [];
            let allCdcErrors = [];

            // 1. Test Source
            const sourceRes = await fetch(`${API_BASE_URL}/api/dms/test-source`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    connectionId: formData.sourceId,
                    options: {
                        connectivityMode: formData.connectivityMode,
                        allocatedPortSource: formData.allocatedPortSource
                    }
                })
            });
            const sourceData = await sourceRes.json();
            if (sourceData.warnings) allWarnings = [...allWarnings, ...sourceData.warnings];
            // Always capture CDC errors from the source test (even if overall test succeeds)
            if (sourceData.cdcErrors?.length > 0) {
                allCdcErrors = [...allCdcErrors, ...sourceData.cdcErrors];
            }

            if (!sourceData.success) {
                setTestSteps(prev => ({ ...prev, source: 'error' }));
                setValidationResult({
                    success: false,
                    stage: 'source',
                    error: sourceData.error || (sourceData.errors && sourceData.errors[0]),
                    errors: sourceData.errors,
                    suggestions: sourceData.suggestions,
                    warnings: allWarnings,
                    cdcErrors: allCdcErrors,
                    cdcReady: allCdcErrors.length === 0
                });
                return;
            }
            setTestSteps(prev => ({ ...prev, source: 'success', target: 'loading' }));

            // 2. Test Target
            const targetRes = await fetch(`${API_BASE_URL}/api/dms/test-target`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    connectionId: formData.targetId,
                    options: {
                        connectivityMode: formData.connectivityMode,
                        allocatedPortTarget: formData.allocatedPortTarget
                    }
                })
            });
            const targetData = await targetRes.json();
            if (targetData.warnings) allWarnings = [...allWarnings, ...targetData.warnings];

            if (!targetData.success) {
                setTestSteps(prev => ({ ...prev, target: 'error' }));
                setValidationResult({
                    success: false,
                    stage: 'target',
                    error: targetData.error || (targetData.errors && targetData.errors[0]),
                    errors: targetData.errors,
                    suggestions: targetData.suggestions,
                    warnings: allWarnings,
                    cdcErrors: allCdcErrors,
                    cdcReady: allCdcErrors.length === 0
                });
                return;
            }
            setTestSteps(prev => ({ ...prev, target: 'success', bridge: 'loading' }));

            // 3. Test Bridge
            const bridgeRes = await fetch(`${API_BASE_URL}/api/dms/test-bridge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sourceId: formData.sourceId,
                    targetId: formData.targetId,
                    options: {
                        connectivityMode: formData.connectivityMode,
                        allocatedPortSource: formData.allocatedPortSource,
                        allocatedPortTarget: formData.allocatedPortTarget
                    }
                })
            });
            const bridgeData = await bridgeRes.json();
            if (bridgeData.warnings) allWarnings = [...allWarnings, ...bridgeData.warnings];
            // Merge any additional CDC errors from the bridge check
            if (bridgeData.cdcErrors?.length > 0) {
                bridgeData.cdcErrors.forEach(e => {
                    if (!allCdcErrors.includes(e)) allCdcErrors.push(e);
                });
            }

            if (!bridgeData.success) {
                setTestSteps(prev => ({ ...prev, bridge: 'error' }));
                setValidationResult({
                    success: false,
                    stage: 'bridge',
                    error: bridgeData.error,
                    recommendations: bridgeData.recommendations,
                    warnings: allWarnings,
                    cdcErrors: allCdcErrors,
                    cdcReady: allCdcErrors.length === 0
                });
                return;
            }

            setTestSteps(prev => ({ ...prev, bridge: 'success' }));
            // success = true BUT Launch Job will be blocked if cdcErrors are present

            // Capture allocated ports into formData so they are passed to handleStartMigration
            if (bridgeData.allocatedPortSource || bridgeData.allocatedPortTarget) {
                setFormData(prev => ({
                    ...prev,
                    allocatedPortSource: bridgeData.allocatedPortSource || prev.allocatedPortSource,
                    allocatedPortTarget: bridgeData.allocatedPortTarget || prev.allocatedPortTarget
                }));
            }

            setValidationResult({
                success: true,
                ...bridgeData,
                warnings: allWarnings,
                cdcErrors: allCdcErrors,
                cdcReady: allCdcErrors.length === 0
            });

        } catch (err) {
            setNotify({ type: 'error', message: err.message });
            setValidationResult({ success: false, error: err.message });
        } finally {
            setLoading(false);
        }
    };

    const handleStartMigration = async (isDraft = false) => {
        setLoading(true);
        try {
            const url = editingJobId
                ? `${API_BASE_URL}/api/dms/pipelines/${editingJobId}`
                : `${API_BASE_URL}/api/dms/pipelines`;

            const method = editingJobId ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.jobName,
                    sourceId: formData.sourceId,
                    targetId: formData.targetId,
                    jobType: formData.migrationType,
                    status: isDraft ? 'draft' : 'pending',
                    options: {
                        cloudRegion: formData.cloudRegion,
                        secureTunnel: formData.secureTunnel,
                        parallelism: formData.parallelism,
                        connectivityMode: formData.connectivityMode,
                        allocatedPortSource: formData.allocatedPortSource,
                        allocatedPortTarget: formData.allocatedPortTarget,
                        selectedDatabases: formData.selectedDatabases || []
                    }
                })
            });
            const data = await res.json();
            if (data.success) {
                setNotify({
                    type: 'success',
                    message: editingJobId
                        ? 'Migration Job Updated'
                        : (isDraft ? 'Migration Draft Saved' : 'Migration Job Created')
                });
                setIsWizardOpen(false);
                setEditContext(null);
                setStep(1);
                setEditingJobId(null);
                fetchJobs();
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            setNotify({ type: 'error', message: err.message });
        } finally {
            setLoading(false);
        }
    };

    const handleCutover = async (jobId) => {
        if (!window.confirm('Start Cutover?')) return;
        try {
            await fetch(`${API_BASE_URL}/api/dms/cutover`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ jobId })
            });
            fetchJobs();
        } catch (e) { console.error(e); }
    };

    const toggleJobSelection = (id) => {
        if (selectedJobs.includes(id)) setSelectedJobs(selectedJobs.filter(j => j !== id));
        else setSelectedJobs([...selectedJobs, id]);
    };

    const handleSelectMultipleJobs = (ids, shouldSelect) => {
        if (shouldSelect) {
            setSelectedJobs(prev => [...new Set([...prev, ...ids])]);
        } else {
            setSelectedJobs(prev => prev.filter(id => !ids.includes(id)));
        }
    };

    const handleDeleteJobs = async (jobIds) => {
        setJobsToDelete(jobIds);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (deleteConfirmText.toLowerCase() !== 'delete') {
            return;
        }

        try {
            for (const jobId of jobsToDelete) {
                await fetch(`${API_BASE_URL}/api/dms/pipelines/${jobId}`, {
                    method: 'DELETE'
                });
            }
            setNotify({ type: 'success', message: `Successfully deleted ${jobsToDelete.length} job(s)` });
            setSelectedJobs([]);

            // Clean up from dms_stopped_jobs in localStorage
            const stoppedJobs = JSON.parse(localStorage.getItem('dms_stopped_jobs') || '[]');
            const updatedStoppedJobs = stoppedJobs.filter(id => !jobsToDelete.includes(id) && !jobsToDelete.map(String).includes(String(id)));
            localStorage.setItem('dms_stopped_jobs', JSON.stringify(updatedStoppedJobs));

            fetchJobs();
        } catch (err) {
            setNotify({ type: 'error', message: 'Failed to delete jobs' });
        } finally {
            setShowDeleteModal(false);
            setDeleteConfirmText('');
            setJobsToDelete([]);
        }
    };

    const handleEditDraft = (job, context = null) => {
        // Populate form with draft data
        setFormData({
            jobName: job.name,
            sourceId: job.source_connection_id || '',
            sourceEngine: job.source_connection?.type || '',
            destinationEngine: job.target_connection?.type || '',
            targetId: job.target_connection_id || '',
            migrationType: job.job_type || 'cdc',
            cloudRegion: job.region || 'us-east-1',
            secureTunnel: false,
            parallelism: 4
        });
        setEditingJobId(job.id);
        setEditContext(context);
        setIsWizardOpen(true);
        setStep(context === 'source' ? 2 : context === 'target' ? 3 : 1);
    };

    const initiateDeleteConnection = (connection) => {
        setConnectionToDelete(connection);
        setShowConnectionDeleteModal(true);
        setConnectionDeleteConfirmText('');
        setConnectionDeleteError(null);
    };

    const confirmDeleteConnection = async () => {
        if (!connectionToDelete || connectionDeleteConfirmText.toLowerCase() !== 'delete') return;

        const isDraft = connectionToDelete.id && String(connectionToDelete.id).startsWith('dms_draft_');
        if (isDraft) {
            setLoading(true);
            try {
                deleteLocalDraft(connectionToDelete.id);
                setNotify({ type: 'success', message: 'Connection Draft deleted successfully' });
                setShowConnectionDeleteModal(false);
                setConnectionToDelete(null);
                setConnectionDeleteConfirmText('');
                setConnectionDeleteError(null);
                fetchConnections();
            } catch (error) {
                setConnectionDeleteError('Failed to delete connection draft');
            } finally {
                setLoading(false);
            }
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/connections/${connectionToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();
            if (data.success) {
                setNotify({ type: 'success', message: 'Connection deleted successfully' });
                setShowConnectionDeleteModal(false);
                setConnectionToDelete(null);
                setConnectionDeleteConfirmText('');
                setConnectionDeleteError(null);
                fetchConnections();
            } else {
                const details = data.details ? `${data.details}` : '';
                setConnectionDeleteError(`${data.error}: ${details}`);
            }
        } catch (error) {
            setConnectionDeleteError('Failed to delete connection: Network error or server unreachable');
        } finally {
            setLoading(false);
        }
    };



    // Drawer toggle handler
    const openDrawer = (drawerName) => {
        setActiveDrawer(drawerName);
    };

    const closeDrawer = () => {
        setActiveDrawer(null);
    };

    const dockItems = [
        { 
            icon: <LayoutDashboard size={20} strokeWidth={1.75} />, 
            label: 'Dashboard', 
            className: activeView === 'dashboard' ? 'active' : '',
            onClick: () => { setActiveView('dashboard'); setValidationResult(null); }
        },
        { 
            icon: <Database size={20} strokeWidth={1.75} />, 
            label: 'Connections', 
            className: activeView === 'connections' ? 'active' : '',
            onClick: () => { setActiveView('connections'); setConnectionsTab('active'); setValidationResult(null); }
        },
        { 
            icon: <ShieldCheck size={20} strokeWidth={1.75} />, 
            label: 'Private Connectivity', 
            className: activeView === 'private-connectivity' ? 'active' : '',
            onClick: () => setActiveView('private-connectivity')
        },
        { 
            icon: <Shuffle size={20} strokeWidth={1.75} />, 
            label: 'Migrations', 
            className: activeView === 'jobs' ? 'active' : '',
            onClick: () => setActiveView('jobs')
        },
        { 
            icon: <Activity size={20} strokeWidth={1.75} />, 
            label: 'Monitor', 
            className: activeView === 'monitor' ? 'active' : '',
            onClick: () => setActiveView('monitor')
        }
    ];

    return (
        <div className="dms-layout">
            <AlertWidget validationResult={validationResult} onClose={() => setValidationResult(null)} />
            <NotificationToast notify={notify} onClose={() => setNotify(null)} />
            {/* Top Header Bar */}
            <header className="dms-header">
                <div className="header-left">
                    <AnimatedLogo />
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '15px' }}>
                        <span style={{ fontSize: '1.6rem', fontWeight: '800', color: 'var(--text-main)', letterSpacing: '0.5px', lineHeight: '1.1' }}>Data Lift</span>
                        <span className="highlighted-caption">Move Data Faster, Migrate Smarter</span>
                    </div>
                </div>
                <div className="header-right" style={{ gap: '1.5rem' }}>
                    <div className="header-ticker-container" style={{ margin: 0 }}>
                        <span className="ticker-badge">Info</span>
                        <div className="ticker-pulse"></div>
                        <div className="ticker-scroll">
                            <span className="ticker-item">
                                Datalift supports secure PostgreSQL migrations between 
                                <span className="ticker-logo-group">
                                    <img src={awsLogo} alt="AWS" className="ticker-logo aws-logo-theme-fix" />
                                    <span className="ticker-comma">, </span>
                                    <img src={azureLogo} alt="Azure" className="ticker-logo" />
                                    <span className="ticker-arrow">→</span>
                                    <img src={gcpLogo} alt="GCP" className="ticker-logo" />
                                </span>
                            </span>
                        </div>
                    </div>
                    <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
                    <div className="profile-dropdown-container">
                        <button className="profile-trigger-btn">
                            {authUser.photoUrl && !imgError ? (
                                <img
                                    src={resolvePhotoUrl(authUser.photoUrl)}
                                    alt="Profile"
                                    referrerPolicy="no-referrer"
                                    style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
                                    onError={() => setImgError(true)}
                                />
                            ) : (
                                <User size={16} />
                            )}
                            <span style={{ marginLeft: '6px' }}>{authUser.name}</span> <ChevronDown size={14} style={{ marginLeft: '4px' }} />
                        </button>
                        <div className="profile-dropdown-menu">
                            <button className="dropdown-item" onClick={() => openDrawer('features')}><Zap size={14} /> Features</button>
                            <button className="dropdown-item" onClick={() => window.open('/#/docs', '_blank')}><Book size={14} /> Documentation</button>
                            <button className="dropdown-item" onClick={() => openDrawer('settings')}><Settings size={14} /> Credentials</button>
                            <button className="dropdown-item" onClick={() => openDrawer('profile')}><User size={14} /> Profile</button>
                            <button className="dropdown-item" onClick={() => { }}><Info size={14} /> About</button>
                            <button className="dropdown-item" onClick={() => { }}><LifeBuoy size={14} /> Support</button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="dms-body">
                {/* Top Dock Navigation */}
                <Dock 
                    items={dockItems} 
                    magnification={70} 
                    distance={200} 
                    panelHeight={68} 
                    baseItemSize={50} 
                />

                {/* Main Content Area */}
                <main className="dms-main">

                    {activeView === 'dashboard' && (
                        <DMSDashboard
                            jobs={jobs}
                            connections={connections}
                            onRefreshJobs={fetchJobs}
                            onRefreshConnections={fetchConnections}
                        />
                    )}

                    {activeView === 'connections' && (
                        <DMSConnections
                            connections={connections}
                            setShowConnectionModal={handleOpenCreateModal}
                            onEdit={handleEditConnection}
                            onRefresh={fetchConnections}
                            onDeleteConnection={initiateDeleteConnection}
                            setNotify={setNotify}
                            currentTab={connectionsTab}
                            setCurrentTab={setConnectionsTab}
                        />
                    )}

                    {activeView === 'private-connectivity' && (
                        <PrivateConnectivity
                            connections={connections}
                            jobs={jobs}
                            setNotify={setNotify}
                            onNavigateToSettings={() => openDrawer('settings')}
                            onNavigateToMigrations={() => setActiveView('jobs')}
                        />
                    )}

                    {activeView === 'jobs' && (
                        <DMSMigrations
                            jobs={jobs}
                            connections={connections}
                            selectedJobs={selectedJobs}
                            toggleJobSelection={toggleJobSelection}
                            handleSelectMultipleJobs={handleSelectMultipleJobs}
                            handleCutover={handleCutover}
                            isWizardOpen={isWizardOpen}
                            setIsWizardOpen={(open) => {
                                setIsWizardOpen(open);
                                if (!open) setEditContext(null);
                            }}
                            editContext={editContext}
                            step={step}
                            setStep={setStep}
                            formData={formData}
                            setFormData={setFormData}
                            loading={loading}
                            validationResult={validationResult}
                            testSteps={testSteps}
                            handleValidateConnection={handleValidateConnection}
                            handleStartMigration={handleStartMigration}
                            onCreateConnection={handleOpenCreateModal}
                            onDeleteJobs={handleDeleteJobs}
                            onEditDraft={handleEditDraft}
                            serverIP={serverIP}
                            showNotification={handleShowNotification}
                            fetchJobs={fetchJobs}
                            onNavigateToSettings={() => setActiveView('settings')}
                            onNavigateToPrivateConnectivity={() => setActiveView('private-connectivity')}
                            vpnStatus={vpnStatus}
                        />
                    )}

                    {activeView === 'connectivity' && (
                        <div className="connectivity-view slide-in">
                            <div className="view-header">
                                <h2>Connectivity & Network Test</h2>
                                <p>Validate your infrastructure and allowlist settings before starting migrations.</p>
                            </div>

                            <ParticleCard className="connectivity-card" enableTilt={true} enableMagnetism={false} clickEffect={false} style={{
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '12px',
                                padding: '2rem',
                                width: '450px',
                                maxWidth: '90vw'
                            }}>
                                {/* Connection Mode Selection */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2.5rem' }}>
                                    <div
                                        onClick={() => setConnectionMode('public')}
                                        style={{
                                            padding: '1.5rem',
                                            borderRadius: '12px',
                                            border: `2px solid ${connectionMode === 'public' ? 'var(--primary-color)' : 'var(--border-color)'}`,
                                            background: connectionMode === 'public' ? 'rgba(147, 51, 234, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.3s',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Network size={32} color={connectionMode === 'public' ? 'var(--primary-color)' : 'var(--text-secondary)'} style={{ marginBottom: '1rem' }} />
                                        <h3 style={{ margin: '0 0 0.5rem 0', color: connectionMode === 'public' ? 'var(--primary-color)' : 'var(--text-primary)' }}>Public Internet</h3>
                                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>Standard connection via IP allowlisting and Security Groups.</p>
                                    </div>
                                    <div
                                        onClick={() => setConnectionMode('private')}
                                        style={{
                                            padding: '1.5rem',
                                            borderRadius: '12px',
                                            border: `2px solid ${connectionMode === 'private' ? 'var(--primary-color)' : 'var(--border-color)'}`,
                                            background: connectionMode === 'private' ? 'rgba(147, 51, 234, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.3s',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Terminal size={32} color={connectionMode === 'private' ? 'var(--primary-color)' : 'var(--text-secondary)'} style={{ marginBottom: '1rem' }} />
                                        <h3 style={{ margin: '0 0 0.5rem 0', color: connectionMode === 'private' ? 'var(--primary-color)' : 'var(--text-primary)' }}>Private Network</h3>
                                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>Secure access via SSH Tunnels (Automatic or Manual).</p>
                                    </div>
                                </div>

                                {connectionMode === 'public' ? (
                                    <>
                                        <div className="ip-info-box" style={{
                                            background: 'var(--bg-input)',
                                            padding: '1.5rem',
                                            borderRadius: '12px',
                                            border: '1px solid var(--border-color)',
                                            textAlign: 'center',
                                            marginBottom: '2rem'
                                        }}>
                                            <small style={{ opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                                                Migration Engine Public IP
                                            </small>
                                            <code style={{ fontSize: '1.5rem', color: 'var(--primary-glow)', fontWeight: 'bold' }}>{serverIP}</code>
                                            <p style={{ marginTop: '1rem', opacity: 0.8, fontSize: '0.9rem' }}>
                                                Add this IP to your database security groups (port 5432 / 3306) to allow the migration server to read data.
                                            </p>
                                        </div>

                                        {/* Connection Guide Steps */}
                                        <div className="connection-guide" style={{ marginBottom: '2rem' }}>
                                            <h4 style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                                                <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'text-bottom' }} />
                                                Steps for Successful Connection
                                            </h4>
                                            <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                                <ParticleCard className="step-card" enableTilt={true} enableMagnetism={false} clickEffect={false} style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '8px', display: 'flex', flexDirection: 'column' }}>
                                                    <h5 style={{ color: '#fbbf24', margin: '0 0 1rem 0' }}>For AWS RDS (Source)</h5>
                                                    <ol style={{ paddingLeft: '1.2rem', margin: '0 0 1rem 0', fontSize: '0.85rem', lineHeight: '1.5', flex: 1 }}>
                                                        <li>Go to <strong>RDS Console</strong> &gt; <strong>Databases</strong>.</li>
                                                        <li>Click your DB instance &gt; <strong>Connectivity & security</strong>.</li>
                                                        <li>Click the active <strong>VPC security group</strong>.</li>
                                                        <li>Add <strong>Inbound Rule</strong>:
                                                            <ul style={{ marginTop: '4px', opacity: 0.8 }}>
                                                                <li>Type: PostgreSQL/MySQL (TCP)</li>
                                                                <li>Source: Custom IP <code>{serverIP}/32</code></li>
                                                            </ul>
                                                        </li>
                                                    </ol>
                                                    <a href="https://console.aws.amazon.com/rds/home" target="_blank" rel="noopener noreferrer"
                                                        style={{
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            width: '100%', padding: '0.75rem', marginTop: 'auto',
                                                            backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24',
                                                            border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '6px',
                                                            textDecoration: 'none', fontSize: '0.9rem', fontWeight: '600',
                                                            transition: 'all 0.2s ease'
                                                        }}>
                                                        Start AWS Console <ExternalLink size={16} style={{ marginLeft: '8px' }} />
                                                    </a>
                                                </ParticleCard>
                                                <ParticleCard className="step-card" enableTilt={true} enableMagnetism={false} clickEffect={false} style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '8px', display: 'flex', flexDirection: 'column' }}>
                                                    <h5 style={{ color: '#3b82f6', margin: '0 0 1rem 0' }}>For Google Cloud SQL (Target)</h5>
                                                    <ol style={{ paddingLeft: '1.2rem', margin: '0 0 1rem 0', fontSize: '0.85rem', lineHeight: '1.5', flex: 1 }}>
                                                        <li>Go to <strong>SQL</strong> &gt; Your Instance.</li>
                                                        <li>Click <strong>Connections</strong> tab &gt; <strong>Networking</strong>.</li>
                                                        <li>Under "Authorized networks", click <strong>Add Network</strong>.</li>
                                                        <li>Enter details:
                                                            <ul style={{ marginTop: '4px', opacity: 0.8 }}>
                                                                <li>Name: "DMS Runner"</li>
                                                                <li>Network: <code>{serverIP}/32</code></li>
                                                            </ul>
                                                        </li>
                                                    </ol>
                                                    <a href="https://console.cloud.google.com/sql/instances" target="_blank" rel="noopener noreferrer"
                                                        style={{
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            width: '100%', padding: '0.75rem', marginTop: 'auto',
                                                            backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6',
                                                            border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px',
                                                            textDecoration: 'none', fontSize: '0.9rem', fontWeight: '600',
                                                            transition: 'all 0.2s ease'
                                                        }}>
                                                        Start Google Cloud Console <ExternalLink size={16} style={{ marginLeft: '8px' }} />
                                                    </a>
                                                </ParticleCard>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="private-connection-guide" style={{ marginBottom: '2rem' }}>
                                        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                                            <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                                                <Terminal size={20} style={{ marginRight: '10px', color: 'var(--primary-color)' }} />
                                                SSH Tunnel Setup (Alternative to IP Allowlisting)
                                            </h4>

                                            <div style={{ padding: '0.75rem', marginBottom: '1.5rem', background: 'rgba(234, 179, 8, 0.1)', borderRadius: '8px', borderLeft: '4px solid #eab308' }}>
                                                <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.6' }}>
                                                    <strong>⚠️ Use this method if:</strong> Your databases are in <strong>private subnets</strong> (no public IP).
                                                    SSH tunnels route traffic through bastion hosts, secure and private.
                                                </p>
                                            </div>
                                            <p style={{ opacity: 0.8, marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                                If you prefer manual setup, configure <strong>two separate SSH tunnels</strong> below.
                                                Ensure you keep the terminals open during migration.
                                            </p>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                                {/* Source Tunnel (AWS) */}
                                                <div style={{ background: 'rgba(251, 191, 36, 0.05)', padding: '1.25rem', borderRadius: '10px', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
                                                    <h5 style={{ color: '#fbbf24', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center' }}>
                                                        <Database size={18} style={{ marginRight: '8px' }} />
                                                        Source Tunnel (AWS RDS)
                                                    </h5>

                                                    <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>AWS Bastion IP</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. 34.123.45.67"
                                                                value={sshSourceConfig.bastionHost}
                                                                onChange={e => setSSHSourceConfig({ ...sshSourceConfig, bastionHost: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Bastion User</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="ec2-user"
                                                                value={sshSourceConfig.bastionUser}
                                                                onChange={e => setSSHSourceConfig({ ...sshSourceConfig, bastionUser: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>SSH Key Path</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. C:/Users/hp/.ssh/aws-key.pem"
                                                                value={sshSourceConfig.keyPath}
                                                                onChange={e => setSSHSourceConfig({ ...sshSourceConfig, keyPath: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Private RDS Endpoint</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="my-db.xxx.rds.amazonaws.com"
                                                                value={sshSourceConfig.dbHostname}
                                                                onChange={e => setSSHSourceConfig({ ...sshSourceConfig, dbHostname: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Local Port</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={sshSourceConfig.localPort}
                                                                onChange={e => setSSHSourceConfig({ ...sshSourceConfig, localPort: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div style={{ background: '#1e1e1e', padding: '0.75rem', borderRadius: '6px', border: '1px solid #333', marginBottom: '0.75rem' }}>
                                                        <small style={{ color: '#888', display: 'block', marginBottom: '0.4rem', fontSize: '0.7rem' }}>Terminal 1:</small>
                                                        <code style={{ color: '#fbbf24', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', display: 'block' }}>
                                                            ssh -i "{sshSourceConfig.keyPath || 'path/to/aws-key.pem'}" -N -L {sshSourceConfig.localPort}:{sshSourceConfig.dbHostname || 'rds-endpoint'}:5432 {sshSourceConfig.bastionUser}@{sshSourceConfig.bastionHost || 'aws-bastion'}
                                                        </code>
                                                    </div>

                                                    <div style={{ padding: '0.75rem', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '6px', fontSize: '0.8rem' }}>
                                                        <strong>Connect DMS Source to:</strong><br />
                                                        Host: <code>127.0.0.1</code> | Port: <code>{sshSourceConfig.localPort}</code>
                                                    </div>
                                                </div>

                                                {/* Target Tunnel (GCP) */}
                                                <div style={{ background: 'rgba(59, 130, 246, 0.05)', padding: '1.25rem', borderRadius: '10px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                                                    <h5 style={{ color: '#3b82f6', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center' }}>
                                                        <Database size={18} style={{ marginRight: '8px' }} />
                                                        Target Tunnel (GCP Cloud SQL)
                                                    </h5>

                                                    <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>GCP Bastion IP</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. 35.234.56.78"
                                                                value={sshTargetConfig.bastionHost}
                                                                onChange={e => setSSHTargetConfig({ ...sshTargetConfig, bastionHost: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Bastion User</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="gcp-user"
                                                                value={sshTargetConfig.bastionUser}
                                                                onChange={e => setSSHTargetConfig({ ...sshTargetConfig, bastionUser: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>SSH Key Path</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. C:/Users/hp/.ssh/gcp-key.pem"
                                                                value={sshTargetConfig.keyPath}
                                                                onChange={e => setSSHTargetConfig({ ...sshTargetConfig, keyPath: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Private Cloud SQL IP</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="10.x.x.x (private IP)"
                                                                value={sshTargetConfig.dbHostname}
                                                                onChange={e => setSSHTargetConfig({ ...sshTargetConfig, dbHostname: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Local Port</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={sshTargetConfig.localPort}
                                                                onChange={e => setSSHTargetConfig({ ...sshTargetConfig, localPort: e.target.value })}
                                                                style={{ fontSize: '0.85rem' }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div style={{ background: '#1e1e1e', padding: '0.75rem', borderRadius: '6px', border: '1px solid #333', marginBottom: '0.75rem' }}>
                                                        <small style={{ color: '#888', display: 'block', marginBottom: '0.4rem', fontSize: '0.7rem' }}>Terminal 2:</small>
                                                        <code style={{ color: '#3b82f6', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', display: 'block' }}>
                                                            ssh -i "{sshTargetConfig.keyPath || 'path/to/gcp-key.pem'}" -N -L {sshTargetConfig.localPort}:{sshTargetConfig.dbHostname || 'cloudsql-ip'}:5432 {sshTargetConfig.bastionUser}@{sshTargetConfig.bastionHost || 'gcp-bastion'}
                                                        </code>
                                                    </div>

                                                    <div style={{ padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '6px', fontSize: '0.8rem' }}>
                                                        <strong>Connect DMS Target to:</strong><br />
                                                        Host: <code>127.0.0.1</code> | Port: <code>{sshTargetConfig.localPort}</code>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
                                                <h5 style={{ margin: '0 0 0.5rem 0', color: '#10b981', display: 'flex', alignItems: 'center' }}>
                                                    <CheckCircle size={18} style={{ marginRight: '8px' }} />
                                                    Next Steps
                                                </h5>
                                                <ol style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem', opacity: 0.9, lineHeight: '1.6' }}>
                                                    <li>Run both SSH commands in separate terminals (keep them running)</li>
                                                    <li>Create Source connection: <code>127.0.0.1:{sshSourceConfig.localPort}</code></li>
                                                    <li>Create Target connection: <code>127.0.0.1:{sshTargetConfig.localPort}</code></li>
                                                    <li>Test connectivity below, then start your migration!</li>
                                                </ol>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="test-tool" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
                                    <div className="form-group">
                                        <label>Test Source Connection</label>
                                        <select
                                            value={formData.sourceId || ''}
                                            onChange={e => setFormData({ ...formData, sourceId: e.target.value })}
                                            className="form-control"
                                        >
                                            <option value="">Select a source...</option>
                                            {connections.filter(c => c.role === 'source').map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Test Target Connection</label>
                                        <select
                                            value={formData.targetId || ''}
                                            onChange={e => setFormData({ ...formData, targetId: e.target.value })}
                                            className="form-control"
                                        >
                                            <option value="">Select a destination...</option>
                                            {connections.filter(c => c.role === 'destination').map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Connectivity Test Component */}
                                {formData.sourceId && formData.targetId && (
                                    <ConnectivityTest
                                        sourceConnection={connections.find(c => c.id === parseInt(formData.sourceId))}
                                        targetConnection={connections.find(c => c.id === parseInt(formData.targetId))}
                                        onTestComplete={(result) => {
                                            if (result.success) {
                                                setValidationResult(result.results.bridge);
                                                setNotify({ type: 'success', message: 'Connectivity test passed! Ready for migration.' });
                                            } else {
                                                setNotify({ type: 'error', message: 'Connectivity test failed. Please check the details and fix the issues.' });
                                            }
                                        }}
                                    />
                                )}

                                {!formData.sourceId || !formData.targetId ? (
                                    <div style={{
                                        padding: '2rem',
                                        textAlign: 'center',
                                        background: 'rgba(255,255,255,0.03)',
                                        borderRadius: '12px',
                                        border: '1px dashed var(--border-color)'
                                    }}>
                                        <Network size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                        <p style={{ opacity: 0.6, margin: 0 }}>
                                            Select source and target connections above to run connectivity test
                                        </p>
                                    </div>
                                ) : null}
                            </ParticleCard>
                        </div>
                    )}

                    {activeView === 'monitor' && (
                        <DMSMonitor
                            jobs={jobs}
                            throughputData={throughputData}
                            selectedMonitorJobId={selectedMonitorJobId}
                            setSelectedMonitorJobId={setSelectedMonitorJobId}
                            logs={logs}
                            serverIP={serverIP}
                            jobMetrics={jobMetrics}
                            fetchJobs={fetchJobs}
                        />
                    )}


                </main>
            </div>

            {/* Auto-save micro-toast */}
            {autoSaveToast && (
                <div className="autosave-toast">
                    <Zap size={13} className="autosave-toast-icon" />
                    <span>Draft Auto-Saved</span>
                </div>
            )}

            {/* Connection Edit Selection Modal */}
            {showConnectionEditSelectionModal && pendingConnectionEdit && (
                <div className="modal-overlay" onClick={handleCloseConnectionEditSelection}>
                    <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>What would you like to edit?</h2>
                            <button className="modal-close" onClick={handleCloseConnectionEditSelection}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>Select the configuration section you want to modify.</p>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <button 
                                    className="btn-secondary-outline" 
                                    style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '1rem' }}
                                    onClick={() => confirmConnectionEditContext('source')}
                                >
                                    📥 Source Connection
                                </button>
                                <button 
                                    className="btn-secondary-outline" 
                                    style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '1rem' }}
                                    onClick={() => confirmConnectionEditContext('destination')}
                                >
                                    📤 Destination Connection
                                </button>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
                            <button className="btn-ghost" onClick={handleCloseConnectionEditSelection}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Multi-step Connection Wizard ─────────────────────────── */}
            <ConnectionWizard
                isOpen={showConnectionModal}
                onClose={handleCloseConnectionModal}
                editContext={connectionEditContext}
                onSaveDraft={(formData) => {
                    const now = new Date();
                    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
                    const draftId = autoSaveDraftId || `dms_draft_${Date.now()}`;
                    saveLocalDraft({
                        ...formData,
                        id: draftId,
                        name: formData.name || `Untitled Draft — ${timeStr}`,
                        options: { is_draft: true, auto_saved: false },
                        platform: 'dms',
                    });
                    setNotify({ type: 'success', message: 'Draft saved!' });
                    fetchConnections();
                    setShowConnectionModal(false);
                }}
                onCreateConnections={handleCreateBothConnections}
                onUpdateSingleConnection={handleUpdateSingleConnection}
                loading={loading}
                cloudRegions={cloudRegions}
                vpnStatus={vpnStatus}
                editingData={editingConnectionId ? connectionFormData : null}
                onNavigateToPrivateConnectivity={() => {
                    handleCloseConnectionModal();
                    setActiveView('private-connectivity');
                }}
            />


            {/* Delete Confirmation Modal */}
            {
                showDeleteModal && (
                    <div className="modal-overlay" onClick={() => {
                        setShowDeleteModal(false);
                        setDeleteConfirmText('');
                        setJobsToDelete([]);
                    }}>
                        <div className="delete-modal-beautified" onClick={(e) => e.stopPropagation()}>
                            <button className="close-btn-minimal" onClick={() => {
                                setShowDeleteModal(false);
                                setDeleteConfirmText('');
                                setJobsToDelete([]);
                            }}><X size={20} /></button>

                            <div className="modal-content-centered">
                                <div className="title-row">
                                    <Trash2 size={24} className="title-icon-red" />
                                    <h2>Confirm Jobs Deletion</h2>
                                </div>

                                <div className="warning-box-modern">
                                    <AlertCircle size={20} className="warning-icon" />
                                    <div className="warning-text">
                                        <p>Are you sure you want to delete {jobsToDelete.length} migration job(s)?</p>
                                        <div className="jobs-list-minimal">
                                            {jobsToDelete.map(jobId => (
                                                <div key={jobId}>• {jobs.find(j => j.id === jobId)?.name || jobId}</div>
                                            ))}
                                        </div>
                                        <span>This action cannot be undone. All associated metadata will be removed.</span>
                                    </div>
                                </div>

                                <div className="confirm-section-centered">
                                    <label>Type <strong className="danger-text-highlight">delete</strong> to confirm:</label>
                                    <input
                                        type="text"
                                        className="confirm-input-modern"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        placeholder="Type 'delete'"
                                        autoFocus
                                    />
                                </div>

                                <div className="modal-actions-row">
                                    <button className="btn-secondary-modern" onClick={() => {
                                        setShowDeleteModal(false);
                                        setDeleteConfirmText('');
                                        setJobsToDelete([]);
                                    }}>Cancel</button>
                                    <button
                                        className={`btn-danger-modern ${deleteConfirmText.toLowerCase() === 'delete' ? 'active' : 'disabled'}`}
                                        onClick={confirmDelete}
                                        disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                                    >
                                        Delete {jobsToDelete.length} Job{jobsToDelete.length > 1 ? 's' : ''}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showConnectionDeleteModal && (
                    <div className="modal-overlay" onClick={() => {
                        setShowConnectionDeleteModal(false);
                        setConnectionDeleteConfirmText('');
                    }}>
                        <div className="delete-modal-beautified" onClick={(e) => e.stopPropagation()}>
                            <button className="close-btn-minimal" onClick={() => {
                                setShowConnectionDeleteModal(false);
                                setConnectionDeleteConfirmText('');
                            }}><X size={20} /></button>

                            <div className="modal-content-centered">
                                <div className="title-row">
                                    <Trash2 size={24} className="title-icon-red" />
                                    <h2>Confirm Connection Deletion</h2>
                                </div>

                                <div className="warning-box-modern">
                                    <AlertCircle size={20} className="warning-icon" />
                                    <div className="warning-text">
                                        <p>Are you sure you want to delete <strong>{connectionToDelete?.name}</strong>?</p>
                                        <span>This connection will be permanently removed. You cannot delete a connection if it's currently used by an active migration job.</span>
                                    </div>
                                </div>

                                <div className="confirm-section-centered">
                                    <label>Type <strong className="danger-text-highlight">delete</strong> to confirm:</label>
                                    <input
                                        type="text"
                                        className="confirm-input-modern"
                                        value={connectionDeleteConfirmText}
                                        onChange={(e) => setConnectionDeleteConfirmText(e.target.value)}
                                        placeholder="Type 'delete'"
                                        autoFocus
                                    />
                                </div>

                                {connectionDeleteError && (
                                    <div className="error-notice-minimal">
                                        <XCircle size={16} />
                                        <span>{connectionDeleteError}</span>
                                    </div>
                                )}

                                <div className="modal-actions-row">
                                    <button className="btn-secondary-modern" onClick={() => {
                                        setShowConnectionDeleteModal(false);
                                        setConnectionDeleteConfirmText('');
                                    }}>Cancel</button>
                                    <button
                                        className={`btn-danger-modern ${connectionDeleteConfirmText.toLowerCase() === 'delete' ? 'active' : 'disabled'}`}
                                        onClick={confirmDeleteConnection}
                                        disabled={connectionDeleteConfirmText.toLowerCase() !== 'delete'}
                                    >
                                        Delete Connection
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Sliding Drawers */}
            {activeDrawer && (
                <div className="drawer-overlay" onClick={closeDrawer}>
                    {activeDrawer === 'settings' && (
                        <SettingsPage onBack={closeDrawer} setActiveView={openDrawer} />
                    )}
                    {activeDrawer === 'profile' && (
                        <ProfilePage
                            onBack={closeDrawer}
                            onOpenSettings={() => {
                                closeDrawer();
                                setShowProfileSettings(true);
                            }}
                        />
                    )}
                    {activeDrawer === 'features' && (
                        <FeaturesPage onBack={closeDrawer} />
                    )}
                </div>
            )}

            {/* Profile Settings Modal */}
            {showProfileSettings && (
                <ProfileSettingsModal
                    user={authUser}
                    onClose={() => setShowProfileSettings(false)}
                    onUpdateUser={() => {
                        // Re-read the freshly stored token so the header avatar
                        // and profile dropdown reflect the change immediately.
                        setAuthUser(readAuthUser());
                        setImgError(false);
                    }}
                />
            )}
        </div>
    );
};

export default EnterpriseDMS;
